import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { MapPin, Phone, Navigation } from "lucide-react";

const STAGE_MARKER_COLORS = {
  new_lead: "#3B82F6",
  contacted: "#8B5CF6",
  estimate_scheduled: "#F59E0B",
  estimate_sent: "#EA580C",
  waiting_approval: "#EF4444",
  approved: "#10B981",
  scheduled: "#06B6D4",
  in_progress: "#F97316",
  completed: "#22C55E",
  paid: "#16A34A",
};

function createIcon(color) {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="background:${color};width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });
}

// Default center: Calgary, AB
const DEFAULT_CENTER = [51.0447, -114.0719];

export default function MapViewPage() {
  const [leads, setLeads] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/leads").then((r) => {
      setLeads(r.data.filter((l) => l.lat && l.lng));
    }).catch(err => console.error("Failed to load leads for map:", err));
  }, []);

  return (
    <div className="space-y-4" data-testid="map-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Map View</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {leads.length} locations shown
          {leads.length === 0 && " - Add coordinates to leads to see them on the map"}
        </p>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(STAGE_MARKER_COLORS).map(([stage, color]) => (
          <div key={stage} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-xs text-zinc-500 capitalize">{stage.replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-zinc-200 overflow-hidden" style={{ height: "calc(100vh - 260px)", minHeight: "400px" }}>
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={11}
          style={{ height: "100%", width: "100%" }}
          data-testid="map-container"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {leads.map((lead) => (
            <Marker
              key={lead.id}
              position={[lead.lat, lead.lng]}
              icon={createIcon(STAGE_MARKER_COLORS[lead.stage] || "#71717A")}
            >
              <Popup>
                <div className="min-w-[200px] p-1">
                  <p className="font-bold text-sm text-zinc-900">{lead.name}</p>
                  {lead.phone && <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {lead.phone}</p>}
                  {lead.address && <p className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {lead.address}</p>}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="text-xs capitalize bg-orange-100 text-orange-700">{lead.stage?.replace(/_/g, " ")}</Badge>
                    {lead.fence_type && <Badge variant="secondary" className="text-xs capitalize">{lead.fence_type.replace(/_/g, " ")}</Badge>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      className="text-xs bg-orange-600 hover:bg-orange-700 text-white h-7"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                    >
                      View Lead
                    </Button>
                    {lead.address && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(lead.address)}`, "_blank")}
                      >
                        <Navigation className="w-3 h-3 mr-1" /> Directions
                      </Button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {leads.length === 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
          <MapPin className="w-8 h-8 text-orange-400 mx-auto mb-2" />
          <p className="text-sm text-orange-700 font-medium">No map data yet</p>
          <p className="text-xs text-orange-600 mt-1">When leads have latitude/longitude coordinates, they'll appear here. You can add coordinates when editing a lead.</p>
        </div>
      )}
    </div>
  );
}
